import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { publicApi } from "@/lib/api";

/**
 * Compact payment badge strip — shown next to every product/service
 * so customers know PayID + PayPal are always accepted.
 *
 * variant: "full" (default, shows QR + alias with copy) | "compact" (icons + text only)
 */
export default function PaymentBadges({ variant = "full", className = "" }) {
  const [cfg, setCfg] = useState({ payid: {}, paypal: {} });

  useEffect(() => {
    publicApi.getConfig().then(setCfg).catch(() => {});
  }, []);

  const alias = cfg?.payid?.alias || "";
  const accountName = cfg?.payid?.account_name || "Cameron Boyle";
  const qr = cfg?.paypal?.qr_url || "";

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  if (variant === "compact") {
    return (
      <div data-testid="payment-badges-compact" className={`flex items-center gap-3 text-xs uppercase tracking-widest ${className}`}>
        <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-white/80">
          <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full" /> PayID
        </span>
        <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-white/80">
          <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full" /> PayPal
        </span>
        <span className="text-white/40 normal-case tracking-normal">accepted</span>
      </div>
    );
  }

  return (
    <div data-testid="payment-badges" className={`border border-white/10 bg-[#141414] p-5 ${className}`}>
      <p className="overline mb-4">Ways to pay</p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="space-y-3 min-w-0">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/50">PayID (AU · no fees)</p>
            <div className="flex items-center gap-2 mt-1">
              <p data-testid="pb-payid" className="font-display text-lg break-all">{alias || "Requested at checkout"}</p>
              {alias && (
                <button onClick={() => copy(alias)} className="text-white/60 hover:text-[#FF4500]" aria-label="Copy PayID">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-xs text-white/40 mt-1">Account name · <span className="text-white/70">{accountName}</span></p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/50">PayPal (worldwide · cards accepted)</p>
            <p className="text-sm text-white/70 mt-1">Scan the QR or use the PayPal button at checkout.</p>
          </div>
        </div>
        {qr && (
          <div className="bg-white p-2 text-center w-fit mx-auto sm:mx-0">
            <img
              data-testid="pb-paypal-qr"
              src={qr}
              alt="PayPal QR — Cam's Koori Art"
              className="w-24 h-24 object-contain"
              loading="lazy"
            />
            <p className="text-black text-[9px] uppercase tracking-widest mt-1">Scan to pay</p>
          </div>
        )}
      </div>
      <p className="text-[11px] text-white/40 mt-4 pt-4 border-t border-white/10">
        Every product & service on CKA accepts both PayID and PayPal. Details finalised at checkout / on quote.
      </p>
    </div>
  );
}
